import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  LayoutDashboard, TrendingUp, FileText, Truck, Users, CreditCard,
  Building2, CheckCheck, PieChart, ArrowDownUp, FileCheck2, Settings,
  LogOut, Flame, ClipboardCheck, Menu, X
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", testid: "nav-dashboard" },
  { label: "Ventas", icon: TrendingUp, path: "/ventas", testid: "nav-ventas" },
  { label: "Facturas", icon: FileText, path: "/facturas", testid: "nav-facturas" },
  { label: "Proveedores", icon: Truck, path: "/proveedores", testid: "nav-proveedores" },
  { label: "Personal", icon: Users, path: "/personal", testid: "nav-personal" },
  { label: "Cuentas por pagar", icon: CreditCard, path: "/cuentas-por-pagar", testid: "nav-cuentas-pagar" },
  { label: "Bancos", icon: Building2, path: "/bancos", testid: "nav-bancos" },
  { label: "Conciliación", icon: CheckCheck, path: "/conciliacion", testid: "nav-conciliacion" },
  { label: "P&L", icon: PieChart, path: "/pnl", testid: "nav-pnl" },
  { label: "Cash Flow", icon: ArrowDownUp, path: "/cash-flow", testid: "nav-cash-flow" },
  { label: "Corte de caja", icon: ClipboardCheck, path: "/cortes-caja", testid: "nav-cortes-caja" },
  { label: "Revisar", icon: FileCheck2, path: "/revisar", testid: "nav-revisar" },
  { label: "Configuración", icon: Settings, path: "/configuracion", testid: "nav-configuracion" },
];

export default function Layout() {
  const { user, logout, org, branchFilter, setBranchFilter } = useAuth();
  const [reviewCount, setReviewCount] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/dashboard/review-count").then((r) => setReviewCount(r.data.open)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {navOpen && (
        <div data-testid="nav-backdrop" className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setNavOpen(false)} />
      )}
      <aside data-testid="sidebar" className={`fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-slate-100 z-50 flex flex-col border-r border-slate-800 transform transition-transform duration-200 ${navOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#D9381E] flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm tracking-tight leading-tight">ITACATE FINANCE</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">Taquería Mexicana</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {(user?.role === "MANAGER" ? NAV.filter((i) => i.path === "/cortes-caja") : NAV).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setNavOpen(false)}
              end={item.path === "/dashboard"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${
                  isActive ? "bg-[#D9381E] text-white font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.path === "/revisar" && reviewCount > 0 && (
                <span data-testid="nav-review-badge" className="bg-amber-500 text-slate-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {reviewCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</span>
            <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}
              className="text-slate-400 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button data-testid="nav-toggle" className="lg:hidden p-1.5 -ml-1 text-slate-600 hover:text-slate-900 transition-colors"
              onClick={() => setNavOpen(!navOpen)} aria-label="Menú">
              {navOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="text-sm text-slate-500 font-medium truncate">
              {org.company?.name || "ITACATE"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden sm:inline">Sucursal:</span>
            <Select value={branchFilter || "all"} onValueChange={(v) => setBranchFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="branch-filter-select" className="w-40 sm:w-56 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="branch-option-all">Consolidado (Todas)</SelectItem>
                {org.branches.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.code || b.id}`}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
